import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Form } from 'react-bootstrap';
import { AdOpsToolProps } from '@Pages/AdOps/adops-constants';
import { Button } from '@Components/Button';
import { Size } from '@Components/designConstants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { useLocationCompany } from '@src/utils/hooks/useLocationCompany';
import { createAmcInstanceIdMapping, getAmcInstanceIdMapping } from '../commerce-ad-ops-utils';

export const AmcInstanceMapping = ({ utils }: AdOpsToolProps) => {
  const company = useLocationCompany();
  const { setError, setModalMessage, setShowModalConfirmation } = utils;

  const [loading, setLoading] = useState(false);
  const [amcInstanceId, setAmcInstanceId] = useState('');

  const {
    data: existingMapping,
    isLoading,
    error
  } = useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['amcInstanceIdMapping', company],
    queryFn: () => getAmcInstanceIdMapping(company ?? ''),
    enabled: Boolean(company)
  });

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load AMC Instance ID mapping",
        message: `Failed to load data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  // Reset the field the instant `company` changes (before its data has loaded) so a client
  // switch via ClientSelector -- which keeps this component mounted, only swapping the URL's
  // company segment -- never leaves the previous company's value on screen. Then seed it once
  // per company once that company's mapping data arrives, so a later background refetch of the
  // *same* company (window refocus, cache staleness) can't silently overwrite in-progress typing.
  const seededCompany = useRef<string | null>(null);
  useEffect(() => {
    if (seededCompany.current === company) return;
    setAmcInstanceId('');
    if (existingMapping) {
      seededCompany.current = company;
      setAmcInstanceId(existingMapping[0]?.amcInstanceId ?? '');
    }
  }, [company, existingMapping]);

  const createMapping = async () => {
    if (!company) return;
    try {
      setLoading(true);
      await createAmcInstanceIdMapping({ company, amcInstanceId });
      setShowModalConfirmation(true);
      setModalMessage('AMC Instance ID Mapping created successfully.');
    } catch (e) {
      setError({ message: e.message, reportError: e });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className='commerce-ad-ops-card' data-testid='amc-instance-mapping'>
      <div className='commerce-ad-ops-card-title'>
        AMC Instance ID Mapping
        <InfoTooltip>
          This company&apos;s existing AMC Instance ID (if any) is loaded automatically. Enter a
          value and save to add or replace it.
        </InfoTooltip>
      </div>
      <Form.Group className='commerce-ad-ops-elem'>
        <Form.Label>AMC Instance ID</Form.Label>
        <Form.Control
          data-testid='amc-instance-id-input'
          value={amcInstanceId}
          placeholder={isLoading ? 'Loading...' : 'AMC Instance ID'}
          onChange={e => setAmcInstanceId(e.target.value)}
        />
      </Form.Group>
      <Button
        size={Size.LG}
        className='commerce-ad-ops-submit'
        data-testid='amc-instance-id-submit'
        loading={loading}
        disabled={!company || isLoading || amcInstanceId.trim() === ''}
        onClick={createMapping}
      >
        Add
      </Button>
    </Card>
  );
};
